import React from 'react';
import { Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  AppBar,
  Typography,
  Divider,
  Button,
  Avatar
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import HistoryIcon from '@mui/icons-material/History';
import LoginIcon from '@mui/icons-material/Login';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import TuneIcon from '@mui/icons-material/Tune';
import BarChartIcon from '@mui/icons-material/BarChart';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import SpeedIcon from '@mui/icons-material/Speed';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import KeyIcon from '@mui/icons-material/Key';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import ExtensionIcon from '@mui/icons-material/Extension';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import TableChartIcon from '@mui/icons-material/TableChart';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import { useTenantAuth } from '../context/TenantAuthContext';

const drawerWidth = 240;

const navItems = [
  { to: '/tenant/profile', label: 'My profile', icon: <AccountCircleIcon />, alwaysWhenAuthed: true, end: true },
  { to: '/tenant', label: 'Dashboard', icon: <DashboardIcon />, permission: 'tenant.dashboard.view', end: true },
  {
    to: '/tenant/dashboard-config',
    label: 'Dashboard layout',
    icon: <ViewQuiltIcon />,
    anyOf: ['tenant.dashboard.config.view', 'tenant.dashboard.config.manage']
  },
  {
    to: '/tenant/analytics',
    label: 'Analytics',
    icon: <BarChartIcon />,
    anyOf: ['tenant.analytics.view', 'tenant.usage.view']
  },
  {
    to: '/tenant/usage-policy',
    label: 'Usage policy',
    icon: <SpeedIcon />,
    anyOf: ['tenant.usage_policies.view', 'tenant.usage.view']
  },
  {
    to: '/tenant/plans',
    label: 'Plans',
    icon: <CardMembershipIcon />,
    permission: 'tenant.plans.view'
  },
  {
    to: '/tenant/subscription',
    label: 'Subscription',
    icon: <SubscriptionsIcon />,
    anyOf: ['tenant.subscriptions.view', 'tenant.plans.view']
  },
  {
    to: '/tenant/billing',
    label: 'Billing',
    icon: <ReceiptLongIcon />,
    permission: 'tenant.billing.view'
  },
  {
    to: '/tenant/knowledge-base',
    label: 'Knowledge base',
    icon: <MenuBookIcon />,
    permission: 'tenant.kb.view'
  },
  {
    to: '/tenant/notification-templates',
    label: 'Notification templates',
    icon: <NotificationsActiveIcon />,
    permission: 'tenant.notification_templates.view'
  },
  {
    to: '/tenant/features',
    label: 'Features',
    icon: <ExtensionIcon />,
    anyOf: ['tenant.features.view', 'tenant.plans.view']
  },
  { to: '/tenant/users', label: 'Users', icon: <PeopleIcon />, permission: 'tenant.users.view' },
  { to: '/tenant/roles', label: 'Roles', icon: <SecurityIcon />, permission: 'tenant.roles.view' },
  { to: '/tenant/api-keys', label: 'API keys', icon: <KeyIcon />, permission: 'tenant.api_keys.manage' },
  { to: '/tenant/permissions', label: 'Permissions', icon: <VpnKeyIcon />, permission: 'tenant.permissions.view' },
  { to: '/tenant/settings', label: 'Settings', icon: <SettingsIcon />, permission: 'tenant.settings.view' },
  { to: '/tenant/audit', label: 'Audit logs', icon: <HistoryIcon />, permission: 'tenant.audit.view' },
  {
    to: '/tenant/sessions',
    label: 'Sessions',
    icon: <LoginIcon />,
    permission: 'tenant.sessions.view'
  },
  {
    to: '/tenant/support',
    label: 'Support',
    icon: <SupportAgentIcon />,
    anyOf: [
      'tenant.support_tickets.view',
      'tenant.support_tickets.create',
      'tenant.support_tickets.manage'
    ]
  },
  {
    to: '/tenant/system-settings',
    label: 'Tenant settings',
    icon: <TuneIcon />,
    anyOf: ['tenant.system_settings.view', 'tenant.system_settings.manage']
  },
  {
    to: '/tenant/webhooks',
    label: 'Webhooks',
    icon: <CloudSyncIcon />,
    anyOf: ['tenant.webhooks.view', 'tenant.webhooks.manage']
  },
  {
    to: '/tenant/export-jobs',
    label: 'Export jobs',
    icon: <TableChartIcon />,
    anyOf: ['tenant.exports.view', 'tenant.exports.manage']
  }
];

export default function TenantLayout() {
  const { user, tenant, ready, logout, hasPermission } = useTenantAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (!ready) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user || !tenant) {
    return <Navigate to="/tenant/login" state={{ from: location.pathname }} replace />;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6" noWrap component="div">
            {tenant.name}{' '}
            <Typography component="span" variant="body2" sx={{ opacity: 0.85, ml: 1 }}>
              ({tenant.slug})
            </Typography>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar src={user.avatar_url || undefined} sx={{ width: 32, height: 32 }} />
            <Typography variant="body2" noWrap>
              {user.email}
            </Typography>
            <Button color="inherit" onClick={() => { logout(); navigate('/tenant/login'); }}>
              Sign out
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box' }
        }}
      >
        <Toolbar />
        <Divider />
        <List>
          {navItems
            .filter((item) => {
              if (item.alwaysWhenAuthed) return true;
              if (item.anyOf) return item.anyOf.some((p) => hasPermission(p));
              return hasPermission(item.permission);
            })
            .map((item) => (
              <ListItemButton
                key={item.to}
                selected={
                  item.end
                    ? location.pathname === item.to
                    : location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)
                }
                onClick={() => navigate(item.to)}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
        </List>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 3, minHeight: '100vh', bgcolor: 'grey.50' }}>
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
}
